import React from 'react';
import { usePageContext } from 'vike-react/usePageContext';
import { CaretRight } from '@phosphor-icons/react';
import { useFormatMessage } from '../../i18n-review/client/Msg';
import { resolveBackofficeLocation } from '../../utils/backoffice/backoffice-location';

/**
 * "All tools > Business Development > Monetisation plan", shown only on sub-pages (pages under a
 * tool that are not tools themselves). Top-level tools don't get one: the nav bar already shows
 * where you are and the current tool's neighbours.
 */
export function BackofficeBreadcrumb() {
  const pageContext = usePageContext();
  const formatMessage = useFormatMessage();
  const urlPathname =
    pageContext?.urlPathname ?? (pageContext as { urlParsed?: { pathname?: string } })?.urlParsed?.pathname ?? '';
  const segments = urlPathname.split('/').filter(Boolean);
  const locale = (pageContext?.locale as string) || (['en', 'fi', 'sv'].includes(segments[0] ?? '') ? segments[0] : 'en');
  const prefix = ['en', 'fi', 'sv'].includes(locale) ? `/${locale}` : '';

  const { tool, subpage } = resolveBackofficeLocation(urlPathname, prefix);
  if (!subpage || !tool) return null;

  const linkClass = 'rounded px-1 py-0.5 text-jc-muted transition-colors hover:text-jc-ink hover:underline';
  return (
    <nav
      aria-label={formatMessage({ id: 'backoffice.nav.breadcrumb', defaultMessage: 'Breadcrumb' })}
      className="border-b border-jc-border/60 bg-jc-surface/50 px-4 py-1.5 sm:px-6"
    >
      <ol className="mx-auto flex max-w-6xl flex-wrap items-center gap-0.5 text-xs">
        <li>
          <a href={`${prefix}/backoffice`} className={linkClass}>
            {formatMessage({ id: 'backoffice.hub.nav', defaultMessage: 'All tools' })}
          </a>
        </li>
        <li aria-hidden className="text-jc-muted">
          <CaretRight className="h-3 w-3" />
        </li>
        <li>
          <a href={`${prefix}${tool.path}`} className={linkClass}>
            {formatMessage({ id: tool.labelKey, defaultMessage: tool.defaultLabel })}
          </a>
        </li>
        <li aria-hidden className="text-jc-muted">
          <CaretRight className="h-3 w-3" />
        </li>
        <li aria-current="page" className="px-1 py-0.5 font-medium text-jc-ink">
          {formatMessage({ id: subpage.labelKey, defaultMessage: subpage.defaultLabel })}
        </li>
      </ol>
    </nav>
  );
}
